import React, { useEffect, useState } from "react";
import {
  User,
  Shirt,
  Clock,
  History,
  Loader,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function StudentDashboard() {
  const student = JSON.parse(localStorage.getItem("student") || "null");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!student) return;

    const isDemo = localStorage.getItem("demoMode") === "true";

    if (isDemo) {
      // Dummy data for demo mode
      setData({
        washesRemaining: 22,
        washesUsed: 8,
        queue: 3,
        latestOrder: {
          id: 1042,
          status: "washing",
          created_at: new Date().toISOString()
        }
      });
      setLoading(false);
      return;
    }

    fetch(`https://hostel-laundry-api.onrender.com/dashboard/summary/${student.usn}`)

      .then((res) => res.json())
      .then((json) => {
        setData(json);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader className="w-8 h-8 text-neutral-900 animate-spin" />
      </div>
    );
  }

  const washesRemaining = data?.washesRemaining ?? 30;
  const washesUsed = data?.washesUsed ?? 0;
  const pct = Math.max(0, Math.min(100, (washesRemaining / 30) * 100));

  const latest = data?.latestOrder || {};

  return (
    <div className="min-h-screen bg-white pb-28">

      {/* ===================== HEADER ===================== */}
      <header className="w-full flex justify-center bg-white/80 backdrop-blur-lg sticky top-0 z-20">
        <div className="max-w-6xl w-full px-6 py-4 flex items-center justify-between border-b border-neutral-200">

          {/* LEFT - PROFILE */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-neutral-900 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-semibold text-neutral-900">
              {student.name}
            </span>
          </div>

          {/* CENTER - APP TITLE */}
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900 select-none hidden md:block">
            HOSTEL LAUNDRY
          </h1>

          {/* RIGHT - HELP DROPDOWN */}
          <div className="relative group cursor-pointer z-20">
            <button className="px-5 py-2 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition">
              Contact
            </button>

            {/* DROPDOWN */}
            <div
              className="
              absolute right-0 mt-2 w-52 z-30
              bg-white shadow-lg border border-neutral-200
              rounded-xl p-4 text-sm
              opacity-0 invisible group-hover:opacity-100 group-hover:visible
              transition-all duration-200
            "
            >
              <p className="font-semibold text-neutral-900 mb-1">Contact Support</p>

              <div className="mt-2 space-y-1">
                <p className="text-neutral-600 flex items-center gap-2">
                  📞 <span>+91 98765 43210</span>
                </p>
                <p className="text-neutral-600 flex items-center gap-2">
                  ✉️ <span>support@laundroid.com</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ===================== CONTENT ===================== */}
      <main className="max-w-6xl mx-auto px-5 mt-6 space-y-6">

        {/* ===================== BANNER SECTION ===================== */}
        <section className="rounded-2xl overflow-hidden border border-neutral-200">
          <div className="relative w-full h-44 md:h-60">
            <img src="/images/banner4.jpg" alt="Laundry service banner" className="w-full h-full object-cover grayscale" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

            <div className="absolute left-6 bottom-6 text-white">
              <h2 className="text-2xl md:text-3xl font-bold">Laundry Made Simple</h2>
              <p className="text-sm md:text-base text-white/70">
                Fast • Reliable • Premium Hostel Laundry Service
              </p>
            </div>
          </div>
        </section>

        {/* ===================== NAV BOXES ===================== */}
        <section>
          <div className="grid grid-cols-4 gap-4">
            <NavCard to="/laundry" icon={Shirt} label="Laundry" />
            <NavCard to="/track-order" icon={Clock} label="Track" />
            <NavCard to="/history" icon={History} label="History" />
            <NavCard to="/profile" icon={User} label="Profile" />
          </div>
        </section>

        {/* ===================== USAGE COUNTER ===================== */}
        <section className="bg-white rounded-2xl border border-neutral-200
          px-10 py-12 flex flex-col md:flex-row items-center justify-evenly gap-12 md:gap-20">

          {/* LEFT - Circular Counter */}
          <div className="relative w-56 h-56 flex-shrink-0">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="50" stroke="#e5e5e5" strokeWidth="8" fill="none" />
              <circle
                cx="60"
                cy="60"
                r="50"
                stroke="#171717"
                strokeWidth="8"
                strokeLinecap="round"
                fill="none"
                strokeDasharray={2 * Math.PI * 50}
                strokeDashoffset={2 * Math.PI * 50 * (1 - pct / 100)}
                className="transition-all duration-700"
              />
            </svg>

            {/* Center Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xs text-neutral-500 mb-0.5">Remaining</span>
              <span className="text-5xl font-bold text-neutral-900">{washesRemaining}</span>
              <span className="text-[11px] text-neutral-400 mt-1">
                {washesUsed} used • 30 total
              </span>
            </div>
          </div>

          {/* Dotted Divider */}
          <div className="hidden md:block h-48 border-l border-dashed border-neutral-300"></div>

          {/* RIGHT - Usage Stats */}
          <div className="flex flex-col gap-5 w-full max-w-xs">
            <div>
              <h2 className="text-xl font-bold text-neutral-900">Laundry Usage</h2>
              <p className="text-sm text-neutral-500 mt-1">
                Overview of your laundry limit & usage this semester.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-neutral-50 rounded-xl px-4 py-4 flex flex-col items-center border border-neutral-100">
                <p className="text-xs text-neutral-500">Used</p>
                <p className="text-xl font-semibold text-neutral-900">{washesUsed}</p>
              </div>

              <div className="bg-neutral-50 rounded-xl px-4 py-4 flex flex-col items-center border border-neutral-100">
                <p className="text-xs text-neutral-500">Remaining</p>
                <p className="text-xl font-semibold text-neutral-900">{washesRemaining}</p>
              </div>

              <div className="bg-neutral-50 rounded-xl px-4 py-4 flex flex-col items-center border border-neutral-100">
                <p className="text-xs text-neutral-500">Queue</p>
                <p className="text-xl font-semibold text-neutral-900">{data.queue}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== TRACKER SECTION ===================== */}
        <section className="bg-white rounded-2xl border border-neutral-200 p-6">
          <h2 className="text-lg font-bold text-neutral-900 mb-4">Order Progress</h2>
          <ThreeDTracker status={latest.status} />
        </section>

      </main>
    </div>
  );
}

/* ---------------------- COMPONENTS ---------------------- */

function NavCard({ to, icon: Icon, label }) {
  return (
    <Link to={to}>
      <div className="bg-white rounded-xl border border-neutral-200 px-4 py-5 flex flex-col items-center hover:border-neutral-400 hover:shadow-sm transition-all group">
        <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center mb-2 group-hover:bg-neutral-900 transition-colors">
          <Icon className="w-5 h-5 text-neutral-700 group-hover:text-white transition-colors" />
        </div>
        <span className="text-xs font-medium text-neutral-700">{label}</span>
      </div>
    </Link>
  );
}

function ThreeDTracker({ status }) {
  const steps = ["received", "washing", "drying", "ready", "delivered"];
  const labels = {
    received: "Received",
    washing: "Washing",
    drying: "Drying",
    ready: "Ready",
    delivered: "Delivered",
  };

  const active = steps.indexOf(status);

  return (
    <div className="flex items-center justify-between mt-6 relative">
      {steps.map((step, i) => {
        const completed = i <= active;

        return (
          <div key={step} className="flex flex-col items-center flex-1 relative">

            {/* Line between nodes */}
            {i !== steps.length - 1 && (
              <div
                className={`absolute top-6 left-1/2 w-full h-px ${
                  completed ? "bg-neutral-900" : "bg-neutral-200"
                }`}
              />
            )}

            {/* Node */}
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all font-semibold text-sm ${
                completed
                  ? "bg-neutral-900 text-white"
                  : "bg-neutral-100 text-neutral-500 border border-neutral-200"
              }`}
            >
              {i + 1}
            </div>

            <span
              className={`mt-2 text-xs font-medium ${
                completed ? "text-neutral-900" : "text-neutral-400"
              }`}
            >
              {labels[step]}
            </span>
          </div>
        );
      })}
    </div>
  );
}
